import { GbdsElement, OnInit, prop } from '@gbds/core/src/ts/core'
import { setLangCountryContext } from '../utils/locale-utils'
import { INITIAL_RENDER } from '../events'
export { INITIAL_RENDER } from '../events'

import { isWebViewApp } from '@gbds/core/src/ts/utils/helper-utils'
import { parseString } from '../utils/dom-string-parser'
import { getNetStorageContentPath, NetStorageEnv } from '../utils/netstorage-paths'
import { createPortableExperiences } from '../utils/portable-experiences'

interface InitialRenderEvent {
	height: number
}

/**
 * @noInheritDoc
 */
export class GbpeFooter extends GbdsElement implements OnInit {
	@prop()
	variation: string

	private readonly templateSuffix = '-global_footer.html'

	protected _styles: string

	// Keeping this private so it doesn't show up in docs. Meant to be a testing/dev override.
	// Looks like `_base_url_override` as an attribute.
	@prop()
	_baseUrlOverride: NetStorageEnv

	constructor(footerBrandStyles: string, private _defaultVariation: string) {
		super()
		this._styles = footerBrandStyles
	}

	async onInit(): Promise<void> {
		if (!isWebViewApp()) {
			const childTemplates = this.getElementsByTagName('template')
			if (this.childElementCount !== 0 && childTemplates.length > 0) {
				this.renderStatic()
				this._addContentRole()
			} else {
				if (this.getAttribute('lazyload-footer')) {
					this.initFooterObserver()
				} else {
					await this.renderDynamic(this.variation ?? this._defaultVariation)
					this._addContentRole()
				}
			}

			// Load external custom colors
			this._loadExternalColors()

			// Set language country context
			setLangCountryContext(this.shadowRoot)
		}
	}

	/**
	 * Adds the role to the global footer if one is not already defined
	 * Used for better accessibility
	 * @private
	 */
	private _addContentRole() {
		if (this.getAttribute('role') === null) {
			this.setAttribute('role', 'contentinfo')
		}
	}
	private renderStatic() {
		this.initShadowDom()
		this.initStaticContent()
	}

	private initShadowDom() {
		this.attachShadow({ mode: 'open' })
		const stylesTag = document.createElement('style')
		stylesTag.textContent =
			this._styles +
			'::slotted(*) { color: inherit; font: inherit; letter-spacing: inherit; line-height: inherit; }'
		this.shadowRoot.appendChild(stylesTag)
	}

	/**
	 * Adds a named slot for every direct light-DOM child with a `slot` attribute (AEM-authored
	 * footer divs), in document order, right after the consumer privacy link, or at the end of
	 * the footer when that link is missing.
	 * @private
	 */
	private addConsentSlots() {
		const slotNames = Array.from(this.children)
			.map(child => child.getAttribute('slot'))
			.filter((name, index, names) => name && names.indexOf(name) === index)
		if (slotNames.length === 0) return

		const privacyLink = Array.from(this.shadowRoot.querySelectorAll('a')).find(a =>
			(a.href || '').includes('consumer-privacy')
		)
		let previous: Element = privacyLink

		slotNames.forEach(name => {
			const existing = Array.from(this.shadowRoot.querySelectorAll('slot')).find(slot => slot.name === name)
			if (existing) {
				previous = existing
				return
			}

			const slot = document.createElement('slot')
			slot.name = name
			if (previous) {
				previous.insertAdjacentElement('afterend', slot)
			} else {
				this.shadowRoot.appendChild(slot)
			}
			previous = slot
		})
	}

	private async renderDynamic(variation: string) {
		const url = getNetStorageContentPath(variation, this.templateSuffix, this._baseUrlOverride)
		try {
			const response = await fetch(url)
			// Need to check if the response is JSON, lower environment's currently return JSON from NetStorage
			const rawText = await response.text()
			const isJson = rawText.includes(`"status":`)
			const html = isJson ? JSON.parse(rawText).html : rawText
			this.initShadowDom()
			this.initFetchedContent(html)
		} catch {
			throw new Error(`The variation ${variation} does not exist at ${url}.`)
		}
	}

	private initFetchedContent(markup: string) {
		const rawContent = parseString(markup)
		const template = rawContent.querySelector('template')
		const content = template.content.cloneNode(true) as HTMLElement
		Array.from(content.children).forEach(c => this.shadowRoot.appendChild(c))
		this.adoptFetchedSlotChildren(
			template.parentElement === rawContent.head ? rawContent.body : template.parentElement
		)
		this.addConsentSlots()
		this.dispatchRendered()
	}

	/**
	 * Moves authored slot divs that sit next to the fetched template (e.g. consent divs rendered
	 * after `</template>`) into this element's light DOM so they can be slotted and stay reachable
	 * by page scripts via `document.getElementById`. Slot names the host page already provides win.
	 * @private
	 */
	private adoptFetchedSlotChildren(container: Element) {
		const existingNames = Array.from(this.children).map(child => child.getAttribute('slot'))
		Array.from(container?.children ?? [])
			.filter(child => child.hasAttribute('slot') && !existingNames.includes(child.getAttribute('slot')))
			.forEach(child => this.appendChild(document.importNode(child, true)))
	}

	private initStaticContent() {
		const templateContents: HTMLTemplateElement = this.querySelector('template')
		const contents = templateContents.content.cloneNode(true)
		this.shadowRoot.appendChild(contents)
		templateContents.remove()
		this.addConsentSlots()
		this.dispatchRendered()
	}

	private dispatchRendered() {
		const detail: InitialRenderEvent = {
			height: this.offsetHeight,
		}

		this.dispatchEvent(new CustomEvent(INITIAL_RENDER, { detail, bubbles: true }))

		// Inject honored footer after rendering
		try {
			const portableExperiences = window?.gbpe || createPortableExperiences()
			if (
				portableExperiences.honoredFooterService &&
				typeof portableExperiences.honoredFooterService.inject === 'function'
			) {
				portableExperiences.honoredFooterService.inject()
			}
		} catch (e) {
			console.warn('HonoredFooterService injection unavailable or failed:', e)
		}
	}

	private initFooterObserver() {
		const footer = document.querySelectorAll('gb-global-footer')
		const customMargin = '50%'
		const observerOptions = { rootMargin: customMargin }

		const intersectionCallback = entries => {
			entries.forEach(async entry => {
				if (entry.isIntersecting) {
					//stop observing
					observer.unobserve(entry.target)

					await this.renderDynamic(this.variation ?? this._defaultVariation)
					this._addContentRole()
				}
			})
		}

		const observer = new IntersectionObserver(intersectionCallback, observerOptions)
		observer.observe(footer[0])
	}

	/**
	 * Loads external CSS by automatically parsing base URL override for brand/region/locale/language
	 */
	public async _loadExternalColors(): Promise<void> {
		// First check if a custom colors link tag already exists in the head
		let cssUrl = this._findExistingCustomColorsLink()

		// If no existing link, build the URL dynamically
		if (!cssUrl) {
			cssUrl = this._buildDynamicColorUrl()
		}

		if (!cssUrl) {
			// Specific warnings already logged in helper methods
			return
		}

		try {
			const response = await fetch(cssUrl)
			if (!response.ok) {
				console.warn(`Custom colors failed to load: HTTP ${response.status} when fetching ${cssUrl}`)
				return
			}

			const cssText = await response.text()

			// Inject styles into the shadow DOM for proper encapsulation
			const styleElement = document.createElement('style')
			styleElement.textContent = cssText
			styleElement.setAttribute('data-source', 'custom-colors')
			this.shadowRoot?.appendChild(styleElement)
		} catch (error) {
			console.error(`Custom colors failed to load: Network error when fetching CSS`, error)
		}
	}

	/**
	 * Checks if a custom colors link tag already exists in the document head
	 * Returns the href if found, null otherwise
	 */
	public _findExistingCustomColorsLink(): string | null {
		// Look for link tags in the head that contain custom colors CSS
		const linkElements = document.head.querySelectorAll('link[rel="stylesheet"]')

		for (const link of linkElements) {
			const href = link.getAttribute('href')
			if (href !== null && href.includes('content/gm/api/services/customcolor.')) {
				// Ensure the href starts with / if it doesn't already
				return href.startsWith('/') ? href : `/${href}`
			}
		}

		return null
	}

	/**
	 * Builds dynamic color CSS URL based on base URL override
	 * Format: /content/gm/api/services/customcolor.{brand}.{region}.{locale}.{language}.css
	 */
	public _buildDynamicColorUrl(): string | null {
		const urlInfo = this._parseBaseUrlOverride()
		if (!urlInfo) return null

		return `/content/gm/api/services/customcolor.${urlInfo.brand}.${urlInfo.region}.${urlInfo.locale}.${urlInfo.language}.css`
	}

	/**
	 * Parses the base URL override to extract brand, region, locale, and language information
	 * Expected format: /content/experience-fragments/{brand}/{region}/{locale}/{language}/...
	 */
	public _parseBaseUrlOverride(): { brand: string; region: string; locale: string; language: string } | null {
		try {
			if (!this._baseUrlOverride) {
				console.warn('Custom colors failed to load: No base URL override provided')
				return null
			}

			// Convert to string if it's not already
			const baseUrl =
				typeof this._baseUrlOverride === 'string' ? this._baseUrlOverride : String(this._baseUrlOverride)

			// Find the consistent part and extract what comes after
			const experienceFragmentsPath = '/content/experience-fragments/'
			const fragmentIndex = baseUrl.indexOf(experienceFragmentsPath)

			if (fragmentIndex === -1) {
				console.warn(
					'Custom colors failed to load: Base URL does not contain expected experience fragments path'
				)
				return null
			}

			// Extract the path after /content/experience-fragments/
			const pathAfterFragments = baseUrl.substring(fragmentIndex + experienceFragmentsPath.length)
			const pathParts = pathAfterFragments.split('/')

			if (pathParts.length < 4) {
				console.warn(
					'Custom colors failed to load: Base URL does not contain enough path segments for brand/region/locale/language'
				)
				return null
			}

			const brand = pathParts[0]
			const region = pathParts[1]
			const locale = pathParts[2]
			const language = pathParts[3]

			if (!brand || !region || !locale || !language) {
				console.warn('Custom colors failed to load: One or more required path segments are empty')
				return null
			}

			return { brand, region, locale, language }
		} catch (error) {
			console.warn('Custom colors failed to load: Error parsing base URL override', error)
			return null
		}
	}
}
